import { useController, Control, RegisterOptions, FieldValues, Path } from "react-hook-form";
import Autocomplete from "../Autocomplete";
import countries from "./countries.json";
import { useEffect, useState } from "react";
import { countryCodeToLabel } from "./utils";

export interface CountryDropdownProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  rules?: RegisterOptions;
  required?: boolean;
}

type Option = { label: string; value: string };

/**
 * CCMS requires the alpha 3 code, but google places returns alpha 2.
 * @see https://www.iso.org/iso-3166-country-codes.html
 */
const dropdownCountries: Option[] = countries.map(({ label, code3: value }) => ({ label, value }));

export default function CountryDropdown<T extends FieldValues>({
  control,
  name,
  rules,
  required
}: CountryDropdownProps<T>) {
  const {
    field: { onChange: onChangeController, value },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ control, name, rules: { required: !!required, ...rules } });
  const [search, setSearch] = useState(countryCodeToLabel(value));

  function onChange(v: Option) {
    onChangeController(v.value);
    setSearch(v.label);
  }

  function onChangeText(text: string) {
    setSearch(text);
  }

  const renderItem = ({ label }: Option) => <span>{label}</span>;

  useEffect(() => {
    setSearch(countryCodeToLabel(value) || "");
  }, [value]);

  const data = search
    ? dropdownCountries.filter(({ label, value }) =>
        [label, value].some((option) => option.toLowerCase().includes(search.toLowerCase()))
      )
    : dropdownCountries;

  return (
    <Autocomplete
      data={data}
      disabled={isSubmitting}
      onSelect={onChange}
      renderItem={renderItem}
      onChangeText={onChangeText}
      reset={() => {
        onChangeController("");
        setSearch("");
      }}
      variant="searchableDropdown"
      query={search}
      error={!!error}
      errorText={error?.message}
      label="Country/Region"
      required={required}
    />
  );
}
